// views/EmailMarketingReport.js
import React, { useState, useEffect } from "react";
import {
    Card,
    CardHeader,
    CardFooter,
    Pagination,
    PaginationItem,
    PaginationLink,
    Table,
    Container,
    Row,
    Col,
    Alert,
    Form,
    FormGroup,
    Input,
    Label,
    Button
} from "reactstrap";
import Header from "components/Headers/Header.js";

const RDStationReport = () => {
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filtros e Paginação
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    const fetchCampaigns = async (currentPage = 1) => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");

            let url = `${process.env.REACT_APP_API_URL}/rd-station/campaigns?limit=10&page=${currentPage}`;

            if (startDate) url += `&startDate=${startDate}`;
            if (endDate) url += `&endDate=${endDate}`;

            const response = await fetch(url, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error("Falha ao buscar dados do RD Station");
            }

            const result = await response.json();

            // Ajuste para nova estrutura de resposta { data, meta }
            setCampaigns(result.data || []);
            setTotalPages(result.meta?.totalPages || 1);
            setPage(currentPage);

        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Carregar ao iniciar e quando mudar a página
    useEffect(() => {
        fetchCampaigns(page);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    const handleFilter = (e) => {
        e.preventDefault();
        setPage(1); // Resetar para primeira página ao filtrar
        fetchCampaigns(1);
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
        }
    };

    // Ordenação
    const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const sortedCampaigns = React.useMemo(() => {
        let sortableItems = [...campaigns];
        if (sortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                // Tratamento especial para taxas (que são strings "20.00")
                if (sortConfig.key === 'openRate' || sortConfig.key === 'clickRate') {
                    aValue = parseFloat(aValue);
                    bValue = parseFloat(bValue);
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [campaigns, sortConfig]);

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return <i className="fas fa-sort text-muted ml-1"></i>;
        return sortConfig.direction === 'asc'
            ? <i className="fas fa-sort-up ml-1"></i>
            : <i className="fas fa-sort-down ml-1"></i>;
    };

    return (
        <>
            <Header />
            <Container className="mt--7" fluid>
                <Row>
                    <div className="col">
                        <Card className="shadow">
                            <CardHeader className="border-0">
                                <Row className="align-items-center">
                                    <Col xs="8">
                                        <h3 className="mb-0">Relatório de Campanha RD (RD Station)</h3>
                                    </Col>
                                    <Col className="text-right" xs="4">
                                        <small className="text-muted">Atualizado em tempo real</small>
                                    </Col>
                                </Row>

                                {/* Filtros */}
                                <Form className="mt-4" onSubmit={handleFilter}>
                                    <Row>
                                        <Col md="4">
                                            <FormGroup>
                                                <Label for="startDate" size="sm">Data Início</Label>
                                                <Input
                                                    type="date"
                                                    name="startDate"
                                                    id="startDate"
                                                    bsSize="sm"
                                                    value={startDate}
                                                    onChange={(e) => setStartDate(e.target.value)}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md="4">
                                            <FormGroup>
                                                <Label for="endDate" size="sm">Data Fim</Label>
                                                <Input
                                                    type="date"
                                                    name="endDate"
                                                    id="endDate"
                                                    bsSize="sm"
                                                    value={endDate}
                                                    onChange={(e) => setEndDate(e.target.value)}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md="4" className="d-flex align-items-end pb-3">
                                            <Button color="primary" size="sm" type="submit" block>
                                                Filtrar
                                            </Button>
                                        </Col>
                                    </Row>
                                </Form>
                            </CardHeader>

                            {error && (
                                <Alert color="danger" className="m-3">
                                    {error}
                                </Alert>
                            )}

                            <Table className="align-items-center table-flush" responsive>
                                <thead className="thead-light">
                                    <tr>
                                        <th scope="col" style={{ cursor: 'pointer' }} onClick={() => handleSort('accountName')}>
                                            Conta {getSortIcon('accountName')}
                                        </th>
                                        <th scope="col" style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>
                                            Campanha {getSortIcon('name')}
                                        </th>
                                        <th scope="col" style={{ cursor: 'pointer' }} onClick={() => handleSort('date')}>
                                            Data {getSortIcon('date')}
                                        </th>
                                        <th scope="col" style={{ cursor: 'pointer' }} onClick={() => handleSort('sends')}>
                                            Enviados {getSortIcon('sends')}
                                        </th>
                                        <th scope="col" style={{ cursor: 'pointer' }} onClick={() => handleSort('openRate')}>
                                            Taxa de Abertura {getSortIcon('openRate')}
                                        </th>
                                        <th scope="col" style={{ cursor: 'pointer' }} onClick={() => handleSort('clickRate')}>
                                            Taxa de Cliques {getSortIcon('clickRate')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan="6" className="text-center p-4">
                                                <i className="fas fa-spinner fa-spin mr-2"></i> Carregando dados...
                                            </td>
                                        </tr>
                                    ) : campaigns.length === 0 ? (
                                        <tr>
                                            <td colSpan="6" className="text-center p-4">
                                                Nenhuma campanha encontrada no período selecionado.
                                            </td>
                                        </tr>
                                    ) : (
                                        sortedCampaigns.map((campaign) => (
                                            <tr key={`${campaign.accountName}-${campaign.id}`}>
                                                <td>
                                                    <span className="badge badge-pill badge-default" style={{ fontSize: '11px' }}>
                                                        {campaign.accountName}
                                                    </span>
                                                </td>
                                                <th scope="row">
                                                    <span className="mb-0 text-sm font-weight-bold">{campaign.name}</span>
                                                </th>
                                                <td>{new Date(campaign.date).toLocaleDateString()}</td>
                                                <td>{campaign.sends.toLocaleString()}</td>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <span className="mr-2">{campaign.opens.toLocaleString()}</span>
                                                        <div style={{ width: "60px" }}>
                                                            <small className={`text-${campaign.openRate > 20 ? 'success' : 'warning'} font-weight-bold`}>
                                                                {campaign.openRate}%
                                                            </small>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <span className="mr-2">{campaign.clicks.toLocaleString()}</span>
                                                        <div>
                                                            <small className="text-muted">({campaign.clickRate}%)</small>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>

                            <CardFooter className="py-4">
                                <nav aria-label="...">
                                    <Pagination
                                        className="pagination justify-content-end mb-0"
                                        listClassName="justify-content-end mb-0"
                                    >
                                        <PaginationItem className={page <= 1 ? "disabled" : ""}>
                                            <PaginationLink
                                                href="#pablo"
                                                onClick={(e) => { e.preventDefault(); handlePageChange(page - 1); }}
                                                tabIndex="-1"
                                            >
                                                <i className="fas fa-angle-left" />
                                                <span className="sr-only">Previous</span>
                                            </PaginationLink>
                                        </PaginationItem>

                                        {/* Lógica de Paginação Resumida (Smart Pagination) */}
                                        {(() => {
                                            const pages = [];
                                            const maxVisibleButtons = 5; // Número máximo de botões numéricos visíveis

                                            if (totalPages <= maxVisibleButtons + 2) {
                                                // Se tiver poucas páginas, mostra todas
                                                for (let i = 1; i <= totalPages; i++) {
                                                    pages.push(i);
                                                }
                                            } else {
                                                // Sempre mostra a primeira página
                                                pages.push(1);

                                                // Adiciona reticências se necessário antes do bloco central
                                                if (page > 3) {
                                                    pages.push('...');
                                                }

                                                // Bloco central (página atual e vizinhos)
                                                let startPage = Math.max(2, page - 1);
                                                let endPage = Math.min(totalPages - 1, page + 1);

                                                // Ajuste para garantir que mostre 3 números no meio se possível
                                                if (page === 1) endPage = 3;
                                                if (page === totalPages) startPage = totalPages - 2;

                                                for (let i = startPage; i <= endPage; i++) {
                                                    if (i > 1 && i < totalPages) {
                                                        pages.push(i);
                                                    }
                                                }

                                                // Adiciona reticências se necessário depois do bloco central
                                                if (page < totalPages - 2) {
                                                    pages.push('...');
                                                }

                                                // Sempre mostra a última página
                                                pages.push(totalPages);
                                            }

                                            return pages.map((p, index) => (
                                                p === '...' ? (
                                                    <li key={`ellipsis-${index}`} className="page-item disabled">
                                                        <span className="page-link" style={{ borderColor: 'transparent', background: 'transparent', color: '#8898aa' }}>...</span>
                                                    </li>
                                                ) : (
                                                    <PaginationItem key={p} className={page === p ? "active" : ""}>
                                                        <PaginationLink
                                                            href="#pablo"
                                                            onClick={(e) => { e.preventDefault(); handlePageChange(p); }}
                                                        >
                                                            {p}
                                                        </PaginationLink>
                                                    </PaginationItem>
                                                )
                                            ));
                                        })()}

                                        <PaginationItem className={page >= totalPages ? "disabled" : ""}>
                                            <PaginationLink
                                                href="#pablo"
                                                onClick={(e) => { e.preventDefault(); handlePageChange(page + 1); }}
                                            >
                                                <i className="fas fa-angle-right" />
                                                <span className="sr-only">Next</span>
                                            </PaginationLink>
                                        </PaginationItem>
                                    </Pagination>
                                </nav>
                            </CardFooter>
                        </Card>
                    </div>
                </Row>
            </Container>
        </>
    );
};

export default RDStationReport;
